import { MutableRefObject, useEffect, useRef, useState } from "react";
import EventDropdown from "./EventDropdown";
import EditPrompt from "./EditPrompt";
import SocialCardWithImage from "./SocialCardWithImage";
import { SocialPostType } from "../../types";
import { cn, getTrueDate } from "@/lib/utils";
import { useInView } from "motion/react";
import { Skeleton } from "@/shared/ui/skeleton.tsx";
import { Badge } from "@/shared/ui/badge.tsx";
import { Card } from "@/shared/ui/card.tsx";
import { getAllPosts, getPlatformPosts } from "@/lib/review-fn";
import PostByPlatform from "./PostByPlatform";
import { useAtomValue } from "jotai";
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms";

type EventHeaderProps = {
  event: SocialPostType,
  container: MutableRefObject<HTMLDivElement | null>,
  isInitial: MutableRefObject<boolean>,
  onInView: (id: string) => void,
  onSchedule: (id: number) => void
  eventIndex: number
}

const EventHeader = ({ event, isInitial, onInView, container, onSchedule, eventIndex }: EventHeaderProps) => {

  const [edit, setEdit] = useState(false)

  const [platform, setPlatform] = useState<string | undefined>(undefined)

  const sectionRef = useRef<HTMLDivElement | null>(null);

  const isInView = useInView(sectionRef, { amount: 0.05, root: container });

  const [isPromptLoading, setIsPromptLoading] = useState(false)

  const theme = useAtomValue(themeAtom)

  useEffect(() => {
    if (isInView && !isInitial.current) {
      onInView(event.event);
    }
  }, [isInView, onInView, event]);

  useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false;
    }
  }, []);

  return (
    <div className={cn("w-full flex flex-col gap-2 p-4 bg-blue-300 rounded-xl transition-all ease-in duration-300",
      platform === "Instagram" && "bg-purple-200",
      platform === "Twitter" && "bg-blue-200",
      platform === "Facebook" && "bg-sky-200",
      platform === "Pinterest" && "bg-rose-300",
    )}
      style={{ backgroundColor: platform === undefined ? `${theme.alternate}80` : "" }}
      id={event.event} ref={sectionRef}>
      <div className={cn("w-full z-20 bg-white flex items-start justify-between bg-transparent overflow-hidden")}>
        <div className="w-20 md:w-[15%] 2xl:w-[12%] aspect-square bg-black flex rounded-xl flex-col items-center justify-center gap-0 md:gap-1 text-white">
          <p className="font-poppins font-medium md:text-lg">{getTrueDate(event.start_date, "MMM")}</p>
          <p className="text-2xl md:text-4xl font-semibold font-header">{getTrueDate(event.start_date, "d")}</p>
        </div>
        <div className="flex flex-col gap-1 flex-1 px-4">
          {!isPromptLoading && <>
            <p className="text-base md:text-lg lg:text-2xl font-montserrat tracking-wide font-semibold">{event.event}</p>
            <span className="flex w-full items-center justify-between">
              <p className="text-xs sm:text-sm font-poppins">{event.pending_posts_count} posts remaining approval</p>
              {event.event_type === "Trends" && <Badge className="bg-white text-black hover:bg-theme-secondary"><span className="hidden sm:block">Trending </span> 🔥</Badge>}
            </span>
          </>}
          {isPromptLoading && (
            <div className='flex flex-col gap-2 flex-1'>
              <Skeleton className='h-7 w-2/3'></Skeleton>
              <Skeleton className='h-5 w-2/3'></Skeleton>
            </div>

          )}
        </div>
        <EventDropdown setEdit={setEdit} event_id={event.event_id} />
      </div>
      <EditPrompt edit={edit} setEdit={setEdit} event_id={event.event_id} setIsPromptLoading={setIsPromptLoading} />
      <div className="flex w-full gap-2">
        <Card
          style={{
            backgroundColor: platform === "Instagram" ? `${theme.alternate}80` : "white"
          }}
          className="flex-1 p-4 flex items-center justify-center cursor-pointer" onClick={() => {
            setPlatform(prev => {
              if (prev === "Instagram") {
                return undefined
              }
              return "Instagram"
            })
          }}>
          <img src="https://cdn.simpleicons.org/instagram" className="w-5 h-5" alt="meta" />
        </Card>
        <Card
          style={{
            backgroundColor: platform === "Twitter" ? `${theme.alternate}80` : "white"
          }}
          className="flex-1 p-4 flex items-center justify-center cursor-pointer" onClick={() => {
            setPlatform(prev => {
              if (prev === "Twitter") {
                return undefined
              }
              return "Twitter"
            })
          }}>
          <img src="https://cdn.simpleicons.org/x" className="w-5 h-5" alt="meta" />
        </Card>
        <Card
          style={{
            backgroundColor: platform === "Facebook" ? `${theme.alternate}80` : "white"
          }}
          className="flex-1 p-4 flex items-center justify-center cursor-pointer" onClick={() => {
            setPlatform(prev => {
              if (prev === "Facebook") {
                return undefined
              }
              return "Facebook"
            })
          }}>
          <img src="https://cdn.simpleicons.org/facebook" className="w-5 h-5" alt="meta" />
        </Card>
        <Card
          style={{
            backgroundColor: platform === "Pinterest" ? `${theme.alternate}80` : "white"
          }}
          className="flex-1 p-4 flex items-center justify-center cursor-pointer" onClick={() => {
            setPlatform(prev => {
              if (prev === "Pinterest") {
                return undefined
              }
              return "Pinterest"
            })
          }}>
          <img src="https://cdn.simpleicons.org/pinterest" className="w-5 h-5" alt="meta" />
        </Card>
      </div>
      <div className="columns-1 2xl:columns-2 w-full space-y-2 gap-2">
        {platform
          ? getPlatformPosts(platform, event.content).map((item) => (
            <SocialCardWithImage
              isPlatformSelected={!!platform}
              key={item.post_id}
              content={item}
              event_name={event.event}
              event_id={event.event_id}
              event_status="Pending"
              isRegenerating={isPromptLoading}
              onSchedule={onSchedule}
              eventIndex={eventIndex}
            />
          ))
          : getAllPosts(event.content).map((item) => (
            <PostByPlatform
              key={item.platform}
              posts={item}
              isPlatformSelected={!!platform}
              event_name={event.event}
              event_id={event.event_id}
              event_status="Pending"
              isRegenerating={isPromptLoading}
              onSchedule={onSchedule}
              eventIndex={eventIndex}
            />
          ))}
      </div>
    </div>
  )
}

export default EventHeader;
